import { useHistory } from 'react-router'
import React, { useCallback, useEffect, useState } from 'react'

import { useActionApi, usePersistedState } from 'hooks'
import Api from 'api'

import moment from 'dayjs'
import ConfirmDetails from 'components/EditPost/ConfirmDetails'
import { entities } from 'reduxStore'
import { EditedJob } from 'components/EditJob'
import FixedLoader from 'components/FixedLoader'
import FixedError from 'components/FixedError'
import { JobPostSuccessModal } from 'components/EditPost/JobPostSuccessModal'

export default function EditJobPostConfirmationPage() {
  const history = useHistory()
  const { mode }: { mode: 'post' | 'edit' | 'repost' } = history.location.state as any
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [createdHireJobId, setCreatedHireJobId] = useState<number | null>(null)
  // Persisted job must be declared before hooks that depend on it
  const [persistedJob, saveJob] = usePersistedState({
    entityName: entities.editedJobPost,
    initialValue: {} as EditedJob
  })

  const [featureError, setFeatureError] = useState<string>('')
  const [featuredSuccessfully, setFeaturedSuccessfully] = useState<boolean>(false)

  const [jobPostingLoading, jobPostingError, postJobApi] = useActionApi({
    apiMethod: Api.postJob,
    onSuccess: useCallback(async (res: any) => {
      if (res && typeof res.hireJobId === 'number') {
        setCreatedHireJobId(res.hireJobId)
        // If user opted to feature on post, trigger enable call now
        try {
          if (persistedJob?.featureOnPost) {
            await Api.featureListing(res.hireJobId);
            setFeaturedSuccessfully(true); // Mark as successfully featured
          }
        } catch (error: any) {
          console.error('[Featured Listing] Failed to enable feature on post:', {
            hireJobId: res.hireJobId,
            error: error.message,
            status: error.status,
            response: error.response?.data,
            timestamp: new Date().toISOString()
          });
          
          // Set error message for display to user
          const errorMsg = error?.response?.data?.error || error?.message || 'Failed to feature listing';
          setFeatureError(`Job posted successfully, but featuring failed: ${errorMsg}. You can feature it later from the job details page.`);
        }
      }
      setIsSuccessModalOpen(true)
    }, [persistedJob])
  })
  const [editJobPostingLoading, editJobPostingError, editJobApi] = useActionApi(
    {
      apiMethod: Api.editJob,
      onSuccess: useCallback(() => {
        if (persistedJob?.id) {
          setCreatedHireJobId(persistedJob.id)
        }
        setIsSuccessModalOpen(true)
      }, [persistedJob?.id])
    }
  )

  const dentistUser: any = usePersistedState({
    entityName: entities.user
  })[0]

  const navigateToEditOnRoute = useCallback(
      (route: any) => {
      history.push({
        pathname: route,
        state: {
          mode: 'edit'
        }
      })
    },
    [history]
  )

  const handleCloseSuccessModal = useCallback(() => {
    setIsSuccessModalOpen(false)
    setFeaturedSuccessfully(false) // Reset for next job posting in same session
    setFeatureError('') // Clear any error messages
    history.replace({ pathname: 'jobBoard' })
  }, [history])

  /*Allows to change date if reposting, need to refactor */

  useEffect(() => {
    if (
      ((!persistedJob.datesAutoEdited && persistedJob.status === 2) ||
      persistedJob.status === 3) && moment(persistedJob.startDate).isBefore(moment())
    ) {
      const startDate = new Date()
      const endDate = new Date(+moment(startDate).add(30, 'days'))
      saveJob({
        ...persistedJob,
        startDate,
        endDate,
        datesAutoEdited: true
      })
    }
  }, [persistedJob.id])

  return (
    <>
      <ConfirmDetails
        mode={mode}
        editedJobPost={persistedJob}
        nextText={'Submit'}
        isNextAvailable={false}
        onPosition={useCallback(() => {
          navigateToEditOnRoute('postPosition')
        }, [navigateToEditOnRoute])}
        onLocation={useCallback(() => {
          navigateToEditOnRoute('postOfficeLocation')
        }, [navigateToEditOnRoute])}
        onAvailability={useCallback(() => {
          navigateToEditOnRoute('postAvailability')
        }, [navigateToEditOnRoute])}
        onDates={useCallback(() => {
          navigateToEditOnRoute('postDates')
        }, [navigateToEditOnRoute])}
        onRequirements={useCallback(() => {
          navigateToEditOnRoute('postCandidateRequirements')
        }, [navigateToEditOnRoute])}
        onTitleDescription={useCallback(() => {
          navigateToEditOnRoute('postTitleDescription')
        }, [navigateToEditOnRoute])}
        onChangeEmail={useCallback(
          (notificationEmail: any) => {
            saveJob({
              ...persistedJob,
              notificationEmail
            })
          },
          [saveJob, persistedJob]
        )}
        onNext={useCallback(() => {
          if (mode === 'post' || mode === 'repost') {
            postJobApi(persistedJob)
          } else {
            editJobApi(persistedJob)
          }
          return ''
        }, [mode, postJobApi, editJobApi, persistedJob, dentistUser.email])}
      />
      <FixedLoader loading={jobPostingLoading || editJobPostingLoading} />
      <FixedError error={jobPostingError || editJobPostingError} />
      <JobPostSuccessModal 
        isOpen={isSuccessModalOpen} 
        onClose={handleCloseSuccessModal}
        hireJobId={createdHireJobId ?? undefined}
        featureError={featureError}
        alreadyFeatured={featuredSuccessfully}
      />
    </>
  )
}
